import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

interface FAQItemData {
  question: string;
  answer: string;
}

const FAQItem: React.FC<{
  faq: FAQItemData;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ faq, isOpen, onToggle }) => {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full py-5 flex items-center justify-between text-left group"
      >
        <span
          className={`font-semibold text-lg transition-colors ${
            isOpen
              ? "text-romanian-blue"
              : "text-gray-900 group-hover:text-romanian-blue"
          }`}
        >
          {faq.question}
        </span>
        <span
          className={`ml-4 flex-shrink-0 p-2 rounded-full transition-all ${
            isOpen
              ? "bg-romanian-blue text-white rotate-180"
              : "bg-gray-100 text-gray-500 group-hover:bg-romanian-yellow"
          }`}
        >
          {isOpen ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? "max-h-96 pb-5" : "max-h-0"
        }`}
      >
        <p className="text-gray-600 leading-relaxed pr-12">{faq.answer}</p>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  const { t } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  // Build FAQ data from translations
  const faqData: FAQItemData[] = [
    { question: t.faq.q1, answer: t.faq.a1 },
    { question: t.faq.q2, answer: t.faq.a2 },
    { question: t.faq.q3, answer: t.faq.a3 },
    { question: t.faq.q4, answer: t.faq.a4 },
    { question: t.faq.q6, answer: t.faq.a6 },
    { question: t.faq.q7, answer: t.faq.a7 },
    { question: t.faq.q8, answer: t.faq.a8 },
    { question: t.faq.q9, answer: t.faq.a9 },
  ];

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Split FAQs into two columns
  const midPoint = Math.ceil(faqData.length / 2);
  const leftFaqs = faqData.slice(0, midPoint);
  const rightFaqs = faqData.slice(midPoint);

  return (
    <section className="section bg-white">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-romanian-red font-semibold text-sm uppercase tracking-wider">
            {t.faq.title}
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-romanian-blue mt-2 mb-4">
            FAQ - {t.faq.subtitle}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-romanian-blue via-romanian-yellow to-romanian-red mx-auto mb-4" />
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t.faq.contactQuestion}
          </p>
        </div>

        {/* FAQ Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left Column */}
          <div className="card p-6 md:p-8">
            {leftFaqs.map((faq, index) => (
              <FAQItem
                key={index}
                faq={faq}
                isOpen={openIndex === index}
                onToggle={() => handleToggle(index)}
              />
            ))}
          </div>

          {/* Right Column */}
          <div className="card p-6 md:p-8">
            {rightFaqs.map((faq, index) => (
              <FAQItem
                key={index + midPoint}
                faq={faq}
                isOpen={openIndex === index + midPoint}
                onToggle={() => handleToggle(index + midPoint)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
